// eslint-disable-next-line @typescript-eslint/naming-convention
import Select, {
  components,
  OptionProps,
  PlaceholderProps,
  StylesConfig,
} from 'react-select'

import { Checkbox } from '../../checkbox'

type Option = {
  value: string
  label: string
}

type TextSize = 'sm' | 'base'

// NOTE: Optionは親コンポーネントから渡す方法も考えたが必要になったら修正する
const OptionComponent = (
  props: OptionProps<Option>,
  textSize: TextSize,
  isAllSelected: boolean,
) => {
  const checked =
    props.data.value === 'select_all' ? isAllSelected : props.isSelected
  return (
    <components.Option {...props}>
      <Checkbox checked={checked} label={props.label} textSize={textSize} />
    </components.Option>
  )
}

// NOTE: customStylesは親コンポーネントから渡す方法も考えたが必要になったら修正する
const defaultCustomStyles: StylesConfig<Option> = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'transparent'
      : provided.backgroundColor,
    color: state.isSelected ? '#000' : provided.color,
  }),
  control: (provided, state) => ({
    ...provided,
    fontSize: 16,
    borderColor: state.isFocused ? '#09909E' : provided.borderColor,
    boxShadow: state.isFocused ? '0 0 0 2px #09909E' : provided.boxShadow,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:hover': {
      borderColor: state.isFocused
        ? '#09909E'
        : provided[':hover']?.borderColor,
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '1rem',
    flexWrap: 'nowrap',
    overflowX: 'auto',
  }),
  multiValue: (provided) => ({
    ...provided,
    flexShrink: 0,
  }),
}

const CustomPlaceholder = (
  props: PlaceholderProps<Option>,
  placeholderIcon?: React.ReactNode,
) => {
  return (
    <components.Placeholder {...props}>
      <div className="flex items-center">
        {placeholderIcon && (
          <span className="mr-2 h-4 w-4">{placeholderIcon}</span>
        )}
        <span className="pt-1">{props.children}</span>
      </div>
    </components.Placeholder>
  )
}

const getSelectedOptions = (options: Option[], selectedValues: string[]) => {
  return options.filter((option) => selectedValues.includes(option.value))
}

type MultiSelectProps = {
  options: Option[]
  value: string[]
  onChange: (selectedValues: string[]) => void
  placeholder?: string
  hasSelectAllOption?: boolean
  selectAllOptionLabel?: string
  textSize?: TextSize
  placeholderIcon?: React.ReactNode
  customStyles?: StylesConfig<Option>
} & Partial<
  Omit<
    React.ComponentProps<typeof Select<Option, true>>,
    'onChange' | 'value' | 'components' | 'styles' | 'isMulti' | 'options'
  >
>

export const MultiSelect = ({
  options,
  value,
  onChange,
  hasSelectAllOption = false,
  selectAllOptionLabel = '全てを選択',
  textSize = 'base',
  placeholderIcon,
  customStyles,
  ...rest
}: MultiSelectProps) => {
  const selectAllOption: Option = {
    label: selectAllOptionLabel,
    value: 'select_all',
  }
  const initialVisibleOptions = hasSelectAllOption
    ? [selectAllOption, ...options]
    : options

  const handleChange: React.ComponentProps<
    typeof Select<Option, true>
  >['onChange'] = (newValue) => {
    const selectedOptions = newValue

    if (hasSelectAllOption) {
      const isSelectAllSelected = selectedOptions.some(
        (option) => option.value === selectAllOption.value,
      )

      if (isSelectAllSelected) {
        const isAllSelected = value.length === options.length
        return onChange(isAllSelected ? [] : options.map((o) => o.value))
      }
    }

    const newSelectedValues = selectedOptions.map((option) => option.value)
    onChange(newSelectedValues)
  }

  const isAllSelected = value.length === options.length
  const selectedOptions = getSelectedOptions(
    initialVisibleOptions,
    Array.isArray(value) ? value : [],
  )

  return (
    <Select<Option, true>
      components={{
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Option: (props) => OptionComponent(props, textSize, isAllSelected),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Placeholder: (props) => CustomPlaceholder(props, placeholderIcon),
      }}
      isMulti
      isSearchable={false}
      onChange={handleChange}
      options={initialVisibleOptions}
      styles={customStyles ?? defaultCustomStyles}
      value={selectedOptions}
      {...rest}
    />
  )
}
